import { motion } from "framer-motion";

import AuthorFollow from "@/components/site/AuthorFollow";

const AUTHOR = {
  name: "Dr. Dennis D. Louden",
  role: "Founder · Educator · Author",
  portrait: "/author.png",
  bio: [
    "Dr. Dennis D. Louden is an educator, U.S. Marine Corps veteran, author, speaker, and lifelong advocate for education and personal transformation. With more than two decades of experience teaching mathematics and adult learners, he has earned a reputation for making complex concepts understandable and helping students build confidence through practical instruction. He holds a Doctor of Education (Ed.D.) in Adult Education and has dedicated his career to improving student success, leadership development, and lifelong learning.",
    "Beyond the classroom, Dr. Louden is passionate about inspiring others through his writing, speaking, and educational programs. His work spans mathematics, leadership, artificial intelligence, personal development, health, and human connection. As an endurance cyclist and diabetes advocate, he lives by the principle \u201cKeep Moving. Keep Living.\u201d\u2014a philosophy that reflects resilience, continuous growth, and the belief that education has the power to change lives.",
  ],
  stats: [
    { k: "Ed.D.", v: "Adult Education" },
    { k: "20+", v: "Years teaching" },
    { k: "USMC", v: "Veteran" },
  ],
};

const COMPANY = [
  "Delta DL Enterprise, LLC is dedicated to empowering individuals, educators, professionals, and organizations through education, leadership development, and innovative learning solutions. Founded by Dr. Dennis D. Louden, our mission is to help people unlock their full potential by combining practical knowledge with modern technology, including artificial intelligence, mathematics education, professional development, and personal growth. We believe that learning should inspire confidence, create opportunities, and transform lives.",
  "Through books, online courses, workshops, coaching, and speaking engagements, Delta DL Enterprise, LLC equips learners with the skills needed to succeed in today's rapidly changing world. Whether improving mathematics achievement, advancing career skills, embracing AI, or developing stronger leadership abilities, we are committed to delivering high-quality educational experiences that make a lasting impact.",
];

export const AboutAuthor = () => (
  <section
    id="author"
    data-testid="author-section"
    className="relative bg-[#050505] px-6 md:px-12 py-28 md:py-40 border-t border-white/10"
  >
    {/* Company overview */}
    <div className="mx-auto mb-28 max-w-[1600px] md:mb-36">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      >
        <span className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]">Who We Are</span>
        <h2 className="mt-6 font-display text-5xl md:text-7xl uppercase leading-[0.9] tracking-tight text-white">
          Delta DL Enterprise, LLC
        </h2>
      </motion.div>

      <div className="mt-12 grid max-w-5xl gap-10 md:grid-cols-2">
        {COMPANY.map((p, i) => (
          <motion.p
            key={i}
            data-testid={`company-paragraph-${i}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.1 }}
            className="text-lg leading-relaxed text-white/70"
          >
            {p}
          </motion.p>
        ))}
      </div>

      <div className="mt-8">
        <a
          href="mailto:Deltadlenterprise2026@gmail.com"
          data-testid="company-email"
          className="text-lg text-[#7C3AED] underline underline-offset-4 transition-colors hover:text-white"
        >
          Deltadlenterprise2026@gmail.com
        </a>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9 }}
        className="mt-14 border-l-2 border-[#7C3AED] pl-6"
      >
        <p data-testid="company-philosophy" className="font-serif-ed text-3xl italic text-white md:text-5xl">
          Keep Moving. Keep Living.
        </p>
        <p className="mt-3 text-xs uppercase tracking-[0.3em] text-white/40">
          Our Guiding Philosophy
        </p>
      </motion.div>
    </div>

    {/* Author */}
    <div className="mx-auto grid max-w-[1600px] items-center gap-16 md:grid-cols-12">
      {/* Portrait — clipped, spotlighted frame */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="md:col-span-5"
      >
        <div className="relative">
          <div className="absolute -inset-3 border border-white/10" aria-hidden />
          <div className="relative overflow-hidden">
            <img
              data-testid="author-portrait"
              src={AUTHOR.portrait}
              alt={AUTHOR.name}
              className="h-[520px] w-full object-cover object-top"
            />
            <span className="absolute bottom-5 left-5 rounded-full bg-black/60 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-white backdrop-blur-sm">
              {AUTHOR.role}
            </span>
          </div>
        </div>
      </motion.div>

      {/* Text */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        className="md:col-span-7"
      >
        <span className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]">
          About the Author
        </span>
        <h2 className="mt-6 font-display text-6xl md:text-8xl uppercase leading-[0.9] tracking-tight text-white">
          {AUTHOR.name}
        </h2>

        <div className="mt-8 space-y-5 max-w-xl">
          {AUTHOR.bio.map((p, i) => (
            <p key={i} className="font-serif-ed text-xl md:text-2xl leading-relaxed text-white/70">
              {p}
            </p>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-3 gap-px border border-white/10 bg-white/10">
          {AUTHOR.stats.map((s) => (
            <div key={s.v} className="bg-[#050505] px-5 py-6" data-testid={`author-stat-${s.k}`}>
              <div className="font-display text-4xl text-white">{s.k}</div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.2em] text-white/40">{s.v}</div>
            </div>
          ))}
        </div>

        <AuthorFollow />
      </motion.div>
    </div>
  </section>
);

export default AboutAuthor;
